import React, { useState, useEffect, useRef, useCallback } from 'react';
import { searchApi } from '../../services/api/search.api';
import ChevronIcon from '../../components/icons/ChevronIcon';
import SearchIcon from '../../components/icons/SearchIcon';
import { CourseOption } from '../../types/course.response';
import { getCourseKey } from '../../utils/courseUtils';

interface SearchbarProps {
  onAddCourse: (course: CourseOption) => void;
}

export default function Searchbar({ onAddCourse }: SearchbarProps) {
  const [query, setQuery] = useState('');
  const [availableCourses, setAvailableCourses] = useState<CourseOption[]>([]);
  const [unavailableCourses, setUnavailableCourses] = useState<CourseOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hoveredCourse, setHoveredCourse] = useState<string | null>(null);
  const [showUnavailable, setShowUnavailable] = useState(false);
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const getHoveredCourse = (): CourseOption | null => {
    if (!hoveredCourse) return null;
    return availableCourses.find(course => getCourseKey(course) === hoveredCourse) || null;
  };

  const debouncedSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) {
      setAvailableCourses([]);
      setUnavailableCourses([]);
      setIsDropdownOpen(false);
      setHoveredCourse(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const results: CourseOption[] = await searchApi.searchCourses(searchQuery);
      
      const available: CourseOption[] = [];
      const unavailable: CourseOption[] = [];
      
      results.forEach(course => {
        if (course.courseState.isSemesterAvailable && course.courseState.isLocationAvailable) {
          available.push(course);
        } else {
          unavailable.push(course);
        }
      });
      setAvailableCourses(available);
      setUnavailableCourses(unavailable);   
      setIsDropdownOpen(true);

      if (results.length > 0) {
        setHoveredCourse(getCourseKey(results[0]));
      } else {
        setHoveredCourse(null);
      }
    } catch (err) {
      setError('Failed to search courses');
      console.error('Search error:', err);
      setHoveredCourse(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setQuery(newQuery);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      debouncedSearch(newQuery);
    }, 200);
  };

  const handleCourseSelect = (courseOption: CourseOption) => {
    if (courseOption.userState.status === 'IN_PROGRESS') {
      console.log('Course already in selected semester');
      return;
    }

    setQuery("");
    setIsDropdownOpen(false);
    setHoveredCourse(null);
    setAvailableCourses([]);
    setUnavailableCourses([]);
    setShowUnavailable(false);

    onAddCourse(courseOption);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isDropdownOpen || availableCourses.length === 0) return;
    
    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        const hoveredCourseObj = getHoveredCourse();
        if (hoveredCourseObj) {
          handleCourseSelect(hoveredCourseObj);
        }
        break;
      
      case 'Escape':
        e.preventDefault();
        setIsDropdownOpen(false);
        setHoveredCourse(null);
        setShowUnavailable(false);
        inputRef.current?.blur();
        break;
        
      case 'ArrowDown':
        e.preventDefault();
        const currentIndex = hoveredCourse ? availableCourses.findIndex(course => getCourseKey(course) === hoveredCourse) : -1;
        const nextIndex = currentIndex < availableCourses.length - 1 ? currentIndex + 1 : 0;
        setHoveredCourse(getCourseKey(availableCourses[nextIndex]));
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        const currentUpIndex = hoveredCourse ? availableCourses.findIndex(course => getCourseKey(course) === hoveredCourse) : 0;
        const prevIndex = currentUpIndex > 0 ? currentUpIndex - 1 : availableCourses.length - 1;
        setHoveredCourse(getCourseKey(availableCourses[prevIndex]));
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setHoveredCourse(null);
        setShowUnavailable(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative">
        {/* Search icon */}
        <SearchIcon 
          width={16} 
          height={16} 
          strokeColor="#9ca3af" 
          strokeWidth={3}
          className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none z-10"
        />
        
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={'Search to add courses'}
          className="h-[34px] w-full bg-white pl-9 pr-3 py-2 relative outline-none box-border rounded-[6px] shadow-[0px_3px_3px_rgba(0,0,0,0.1)] border border-[#e0e0e0] transition-all duration-200 ease-in-out focus:border-blue-500 focus:shadow-md"
        />
        
        {/* Loading indicator */}
        {isLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
          </div>
        )}
      </div>

      {/* Dropdown results */}
      {isDropdownOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-80 overflow-y-auto z-50">
          {error ? (
            <div className="px-3 py-2 text-red-600 text-sm">
              {error}
            </div>
          ) : availableCourses.length === 0 && unavailableCourses.length === 0 && !isLoading ? (
            <div className="px-3 py-2 text-gray-500 text-sm">
              No courses found
            </div>
          ) : (
            <>
              {/* Available Courses */}
              {availableCourses.map((course) => {
                const key = getCourseKey(course);
                const isHovered = key === hoveredCourse;
                
                return (
                  <button
                    key={key}
                    onClick={() => handleCourseSelect(course)}
                    onMouseEnter={() => setHoveredCourse(key)}
                    onMouseLeave={() => setHoveredCourse(null)}
                    className={`w-full px-3 py-2 text-left border-b border-gray-100 focus:outline-none transition-colors duration-150 ${
                      isHovered
                        ? 'bg-blue-50 border-blue-200'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-gray-900 flex-shrink-0">
                          {course.courseInfo.subject} {course.courseInfo.number}
                        </span>
                        <span className="text-sm text-gray-600 truncate flex-1">
                          {course.courseInfo.titleShort}
                        </span>
                      </div>
                      {course.topic && (
                        <span className="text-xs text-gray-500 truncate">
                          {course.topic}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}

              {/* Show Unavailable Toggle Button */}
              {unavailableCourses.length > 0 && (
                <button
                  onClick={() => setShowUnavailable(!showUnavailable)}
                  className="w-full px-3 py-2 text-left border-b border-gray-100 focus:outline-none transition-colors duration-150 hover:bg-gray-50 text-blue-600 text-sm flex items-center justify-between"
                >
                  <span>
                    {showUnavailable ? 'Hide unavailable courses' : `Show unavailable courses (${unavailableCourses.length})`}
                  </span>
                  <ChevronIcon 
                    direction={showUnavailable ? 'up' : 'down'} 
                    className="w-5 h-5 text-blue-600" 
                  />
                </button>
              )}

              {/* Unavailable Courses */}
              {showUnavailable && unavailableCourses.map((course) => {
                const key = getCourseKey(course);
                return (
                  <div
                    key={`unavailable-${key}`}
                    className="w-full px-3 py-2 text-left border-b border-gray-100 last:border-b-0 cursor-not-allowed opacity-50 bg-gray-50"
                  >
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-gray-500 flex-shrink-0">
                          {course.courseInfo.subject} {course.courseInfo.number}
                        </span>
                        <span className="text-sm text-gray-400 truncate flex-1">
                          {course.courseInfo.titleShort}
                        </span>
                      </div>
                      {course.topic && (
                        <span className="text-xs text-gray-400 truncate">
                          {course.topic}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}
